"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zip = exports.sortByDynamicField = exports.getIntersection = exports.hasIntersection = exports.groupBy = void 0;
/**
 * Performs a groupBy function on the items passed in using the keyFunc to determine which field to group by.
 *
 * @param items The items to make groups out of.
 * @param keyFunc The function to take the item and determine what the key for grouping is.
 * @returns A Map which has the key based on the KeyFunc and an array of the corresponding items that have that key.
 */
function groupBy(items, keyFunc) {
    return Array.from(items).reduce((acc, item) => {
        const key = keyFunc(item);
        if (!acc.has(key)) {
            acc.set(key, []);
        }
        acc.get(key).push(item);
        return acc;
    }, new Map());
}
exports.groupBy = groupBy;
/**
 * Whether there is an intersection between the two iterables.
 *
 * @param a The first iterable.
 * @param b The second iterable.
 *
 * @returns Whether the two iterables have an intersection.
 */
function hasIntersection(a, b) {
    const set1 = new Set(a);
    for (const item of b) {
        if (set1.has(item)) {
            return true;
        }
    }
    return false;
}
exports.hasIntersection = hasIntersection;
/**
 * Finding the intersection between the two iterables.
 *
 * @param list1 The first iterable.
 * @param list2 The second iterable.
 *
 * @returns The values that are the intersection between the two iterables.
 */
function getIntersection(list1, list2) {
    const set2 = new Set(list2);
    const result = [];
    for (const item of list1) {
        if (set2.has(item)) {
            result.push(item);
        }
    }
    return result;
}
exports.getIntersection = getIntersection;
/**
 * Sorts the objects based on the field specified.
 *
 * @param items The items to sort.
 * @param field The field to sort the items based on.
 * @param order The order to use with the field to sort the array on.
 *
 * @returns A new array that's a copy which is sorted.
 */
function sortByDynamicField(items, field, order = "asc", lastValue = undefined) {
    return [...items].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];
        if (aValue == bValue) {
            return 0;
        }
        // Whenever there is undefined have it be the last set of values (asc or desc irrespective).
        // This can be changed to a parameter for where it goes, but for now this is by default.
        if (aValue == undefined || aValue == lastValue) {
            return 1;
        }
        if (bValue == undefined || bValue == lastValue) {
            return -1;
        }
        const sortValue = aValue < bValue ? -1 : 1;
        return order === "asc" ? sortValue : -1 * sortValue;
    });
}
exports.sortByDynamicField = sortByDynamicField;
function* zip(...toZip) {
    // Get iterators for all of the iterables.
    const iterators = toZip.map((i) => i[Symbol.iterator]());
    while (true) {
        // Advance all of the iterators.
        const results = iterators.map((i) => i.next());
        // If any of the iterators are done, we should stop.
        if (results.some(({ done }) => done)) {
            break;
        }
        // We can assert the yield type, since we know none
        // of the iterators are done.
        yield results.map(({ value }) => value);
    }
}
exports.zip = zip;
